import type { SiteContent } from "./types";

/**
 * Русскоязычный контент сайта Woxel.
 *
 * Тексты вынесены из компонентов, чтобы их можно было править в одном месте.
 * При добавлении English создать `en.ts` с той же структурой.
 *
 * ВАЖНО: Woxel — хобби-мастерская (НЕ лаборатория). Тон: «мастерю для себя».
 */
export const ru: SiteContent = {
  site: {
    name: "Woxel Works",
    tagline: "Хобби-мастерская",
    description:
      "Woxel Works — хобби-мастерская, где я собираю всё, чем увлекаюсь: игровые серверы, приложения, ботов и дополнения к играм.",
  },

  header: {
    nav: [
      { label: "Главная", href: "/" },
      { label: "Игровые серверы", href: "/games" },
      { label: "Приложения и боты", href: "/apps" },
      { label: "Дополнения", href: "/addons" },
      { label: "О Woxel Works", href: "/about" },
    ],
  },

  home: {
    greeting: {
      title: "Хобби-мастерская",
      subtitle:
        "Woxel Works — место, где хобби превращается в работающие вещи. Всё делается для души, без коммерции и сроков.",
      cta: {
        label: "Подробнее о Woxel Works",
        href: "/about",
      },
    },
    directionsTitle: "Чем занимается мастерская",
    directionsSubtitle:
      "Три направления, в которых всё и происходит.",
  },

  about: {
    title: "О Woxel Works",
    teamTitle: "Кто ведёт мастерскую",
    team: [
      {
        name: "MrFras",
        role: "Основатель и ведущий разработчик",
        avatar: "/api/files/avatars/d4038976f82de5c0e558d5cf0001d933.jpg",
      },
      {
        name: "LeB0uN",
        role: "Креативный помощник и основатель Discord-сервера",
      },
    ],
    paragraphs: [
      "Woxel Works — хобби-мастерская, в которой я мастерю и поддерживаю всё, что мне интересно в мире игр и технологий: от игровых серверов и приложений до модов, ботов и мелочей вокруг них.",
      "Раньше это была MFS-STUDIO.RU — студия, где мы с друзьями делали разные проекты ради хобби и заработка. Со временем команда разошлась, и я остался единственным ведущим разработчиком. Woxel — это ребрендинг: теперь это личное пространство для разработки.",
      "Здесь нет коммерции, дедлайнов и обязательств перед кем-либо. Всё, что появляется под брендом Woxel, делается на энтузиазме — для себя, для друзей или ради интереса разобраться в новой технологии. Это не компания и не студия, а скорее личная мастерская, где хобби растёт вместе со мной.",
      "Инфраструктура, серверы и сети — моя личная разработка, она не входит в Woxel и здесь не показывается. На этом сайте — только то, что действительно является частью моей мастерской.",
    ],
  },

  pages: {
    about: "О Woxel Works",
    admin: "Панель управления",
    terms: "Условия использования",
    privacy: "Политика конфиденциальности",
    login: "Вход",
    register: "Регистрация",
    profile: "Профиль",
    forgot: "Восстановление пароля",
    reset: "Новый пароль",
    verify: "Подтверждение почты",
  },

  directions: [
    {
      key: "games",
      href: "/games",
      icon: "Gamepad2",
      title: "Игровые серверы",
      short:
        "Игровые миры для совместной игры. Minecraft, Garry's Mod, Rust, Satisfactory, BeamNG и другие — настроенные, стабильные и открытые для друзей.",
      full: [
        "Под брендом Woxel я поднимаю и обслуживаю игровые серверы для себя и друзей. Это не публичные проекты с сотнями игроков, а уютные миры для совместной игры: Minecraft с модами, Garry's Mod с аддонами, Rust, Satisfactory, BeamNG и другие.",
        "Каждый сервер — это не просто «кнопка запуска»: настройка, моды, плагины, резервные копии, обновления. Всё делается вручную и под конкретную компанию, которая будет играть.",
      ],
      tags: ["Minecraft", "Garry's Mod", "Rust", "Satisfactory", "BeamNG.drive"],
      projects: [
        {
          id: "mcraft",
          name: "Woxel Craft",
          icon: "Pickaxe",
          status: "active",
          description:
            "Сервер Minecraft с модами для небольшой компании: сборка под совместное выживание, свои плагины и регулярные бэкапы.",
          devblog: [
            {
              id: "mcraft-1",
              title: "Собрали сборку под новый сезон",
              date: "2026-09-28",
              body: "Привет! Полностью пересобрали модовую сборку под новый сезон.\n\n**Что изменилось:**\n\n- Обновили ядро и основные моды до актуальных версий\n- Убрали лишнее, что не прижилось\n- Добавили пару технических модов\n\nТема сезона — спокойная стройка и автоматизация без гонки. Сервер уже открыт для своих.",
            },
            {
              id: "mcraft-2",
              title: "Настроили автобэкапы",
              date: "2026-09-12",
              body: "Подключили автоматические резервные копии раз в час с хранением за неделю. Теперь потерять мир из-за сбоя маловероятно.\n\nЗаодно починили пару старых багов с плагинами и ускорили прогрузку чанков.",
            },
          ],
        },
        {
          id: "gmod",
          name: "Gmod-сервер",
          icon: "Blocks",
          status: "development",
          description:
            "Сервер Garry's Mod с набором аддонов для весёлых вечеров: режимы, свои карты и лёгкая автоматизация.",
          devblog: [
            {
              id: "gmod-1",
              title: "Собираем список аддонов",
              date: "2026-09-20",
              body: "Готовим сервер Garry's Mod. Сейчас собираю список аддонов: часть — проверенные временем, часть — свои наработки.\n\nЦель — не перегрузить, а сделать так, чтобы было интересно и стабильно.",
            },
          ],
        },
        {
          id: "rust",
          name: "Rust",
          icon: "Hammer",
          status: "planned",
          description:
            "Планирую поднять Rust для небольшой компании. Пока в стадии подбора конфигурации и модов.",
          devblog: [],
        },
        {
          id: "satisfactory",
          name: "Satisfactory",
          icon: "Factory",
          status: "active",
          description:
            "Кооперативная фабрика для друзей: общий мир, где строим заводы и разбираемся в логистике.",
          devblog: [
            {
              id: "satis-1",
              title: "Запустили общий мир",
              date: "2026-08-30",
              body: "Стартовали новый общий мир Satisfactory. Договорились о зонах: каждый строит свой узел, но логистику тянем вместе.\n\nПервая цель — выйти на стабильное производство продвинутых деталей.",
            },
          ],
        },
        {
          id: "beamng",
          name: "BeamNG.drive",
          icon: "Car",
          status: "frozen",
          description:
            "Сервер для покатушек и краш-тестов в BeamNG.drive. Пока на паузе — вернёмся, когда наберётся компания.",
          devblog: [],
        },
      ],
      news: [
        {
          id: "games-news-1",
          title: "Новый сезон Minecraft открыт",
          date: "2026-09-28",
          excerpt:
            "Модовая сборка пересобрана под новый сезон. Тема — спокойная стройка и автоматизация.",
          body: "Новый сезон на **Woxel Craft** открыт.\n\nМы полностью пересобрали сборку: обновили ядро и моды, убрали лишнее и добавили пару технических модов для автоматизации.\n\nТема сезона — спокойная стройка без гонки. Добро пожаловать, свои!",
        },
        {
          id: "games-news-2",
          title: "Автобэкапы на всех серверах",
          date: "2026-09-12",
          excerpt:
            "Настроили автоматические резервные копии с хранением за неделю. Миры теперь под защитой.",
          body: "Подключили автоматические бэкапы: раз в час, с хранением за неделю.\n\nЭто страховка от сбоев и случайных разрушений. Плюс заодно подчистили старые баги и ускорили сервер.",
        },
        {
          id: "games-news-3",
          title: "Готовим Gmod-сервер",
          date: "2026-09-20",
          excerpt:
            "Собираем список аддонов для Garry's Mod: проверенные временем и свои наработки.",
          body: "В работе сервер **Garry's Mod**.\n\nСейчас формирую список аддонов: часть берём проверенные, часть — свои. Главное — не перегрузить и сохранить стабильность.\n\nСледите за обновлениями в devblog проекта.",
        },
        {
          id: "games-news-4",
          title: "Общий мир Satisfactory запущен",
          date: "2026-08-30",
          excerpt:
            "Стартовали кооперативную фабрику: общий мир, зоны каждого и общая логистика.",
          body: "Запустили общий мир **Satisfactory**.\n\nДоговорились о зонах: каждый строит свой узел, а логистику тянем вместе. Первая цель — стабильное производство продвинутых деталей.",
        },
        {
          id: "games-news-5",
          title: "Rust — в планах",
          date: "2026-08-15",
          excerpt:
            "Подбираю конфигурацию и моды для Rust. Сервер появится, когда наберётся компания.",
          body: "**Rust** пока в планах.\n\nПодбираю конфигурацию и моды под небольшую компанию. Поднимать сервер под двоих смысла мало, так что ждём, пока наберётся состав.",
        },
      ],
    },

    {
      key: "apps",
      href: "/apps",
      icon: "AppWindow",
      title: "Приложения и сервисы, боты",
      short:
        "Мессенджеры, лаунчеры и боты для Discord и Telegram — инструменты, которые делают общение и запуск игр удобнее.",
      full: [
        "Кроме серверов, я делаю приложения и ботов, которые закрывают конкретные задачи сообщества. Среди проектов — Voxiss, экспериментальный мессенджер-аналог Discord, собственные лаунчеры для игр и боты для Discord и Telegram.",
        "Это не продукты для рынка, а рабочие инструменты под свои нужды: автоматизация, оповещения, удобный вход в игру, общение вне больших платформ. Каждое приложение рождается из реальной потребности, а не из желания «сделать как у всех».",
      ],
      tags: ["Voxiss", "Discord-боты", "Telegram-боты", "Лаунчеры"],
      projects: [
        {
          id: "voxiss",
          name: "Voxiss",
          icon: "MessageSquare",
          status: "development",
          description:
            "Экспериментальный мессенджер — попытка собрать своё общение вне больших платформ: каналы, голос, история.",
          devblog: [
            {
              id: "voxiss-1",
              title: "Прототип каналов и сообщений",
              date: "2026-09-25",
              body: "Собрал базовый прототип **Voxiss**: серверная часть принимает сообщения, клиент их показывает в каналах.\n\nПока это черновик без голоса и вложений, но основа для общения уже работает. Следующий шаг — присутствие и уведомления.",
            },
            {
              id: "voxiss-2",
              title: "Выбор стека и архитектуры",
              date: "2026-09-05",
              body: "Определился со стеком для **Voxiss**.\n\nСтавка на то, что уже знаю, плюс один новый инструмент для realtime. Главное правило — не усложнять раньше времени: сначала рабочий минимум, потом остальное.",
            },
          ],
        },
        {
          id: "discord-bot",
          name: "Discord-бот Woxel",
          icon: "Bot",
          status: "active",
          description:
            "Бот для Discord: оповещения о запуске серверов, статусы, роли по играм и мелкая автоматизация.",
          devblog: [
            {
              id: "dbot-1",
              title: "Оповещения о запуске серверов",
              date: "2026-09-18",
              body: "Добавил в бота команду и авто-оповещения о запуске игровых серверов.\n\nТеперь, когда поднимается сервер, в канал улетает сообщение с адресом и версией. Меньше ручных «а сервер поднят?».",
            },
          ],
        },
        {
          id: "telegram-bot",
          name: "Telegram-бот",
          icon: "Send",
          status: "planned",
          description:
            "Бот для Telegram: быстрые уведомления и связка с сервисами Woxel, когда нет доступа к Discord.",
          devblog: [],
        },
        {
          id: "launcher",
          name: "Игровой лаунчер",
          icon: "Rocket",
          status: "development",
          description:
            "Собственный лаунчер, чтобы удобно входить в игру: авторизация, обновления, запуск в один клик.",
          devblog: [
            {
              id: "launcher-1",
              title: "Авторизация и чек обновлений",
              date: "2026-09-10",
              body: "В лаунчере появилась авторизация и проверка обновлений.\n\nЛаунчер входит в аккаунт, смотрит версию клиента и докачивает недостающие файлы. Пока минимум, но уже удобнее, чем запускать вручную.",
            },
          ],
        },
      ],
      news: [
        {
          id: "apps-news-1",
          title: "Voxiss: работает прототип",
          date: "2026-09-25",
          excerpt: "Базовое общение в каналах уже функционирует. Дальше — присутствие и уведомления.",
          body: "**Voxiss** получил рабочий прототип каналов и сообщений.\n\nЭто ещё черновик: без голоса и вложений. Но основа для общения уже есть, и это главное. Следующие шаги — присутствие пользователей и уведомления.",
        },
        {
          id: "apps-news-2",
          title: "Оповещения о запуске серверов в Discord",
          date: "2026-09-18",
          excerpt: "Бот Woxel теперь сам сообщает в канал, когда игровой сервер поднят.",
          body: "Дискорд-бот научился уведомлять о запуске серверов.\n\nКак только сервер поднимается, в канал приходит сообщение с адресом и версией. Больше не нужно спрашивать в чате, поднят ли сервер.",
        },
        {
          id: "apps-news-3",
          title: "Лаунчер: авторизация и обновления",
          date: "2026-09-10",
          excerpt: "В лаунчере появились вход в аккаунт и проверка версии клиента.",
          body: "Игровой лаунчер научился авторизовать пользователя и проверять обновления.\n\nОн входит в аккаунт, сверяет версию клиента и докачивает недостающие файлы. Пока базово, но запуск становится проще.",
        },
        {
          id: "apps-news-4",
          title: "Определён стек для Voxiss",
          date: "2026-09-05",
          excerpt: "Выбрана архитектура мессенджера: сначала рабочий минимум, потом всё остальное.",
          body: "По **Voxiss** определился со стеком и архитектурой.\n\nБеру то, что уже уверенно знаю, плюс один инструмент под realtime. Принцип простой: сначала минимально рабочая версия, затем усложнение.",
        },
        {
          id: "apps-news-5",
          title: "Telegram-бот — в планах",
          date: "2026-08-28",
          excerpt: "Планирую бота для Telegram как альтернативный канал уведомлений.",
          body: "**Telegram-бот** пока в планах.\n\nИдея — продублировать важные уведомления в Telegram для тех, кому удобнее там. Сделаю, когда основная автоматизация в Discord будет стабильной.",
        },
      ],
    },

    {
      key: "addons",
      href: "/addons",
      icon: "Puzzle",
      title: "Дополнения к играм",
      short:
        "Аддоны для Garry's Mod, моды для BeamNG.drive, поезда и логика в Stormworks — игровой контент, который расширяет то, во что я играю.",
      full: [
        "Woxel — это ещё и творчество внутри игр. Я делаю аддоны для Garry's Mod, моды для BeamNG.drive, а также собираю поезда с проработанной логикой и моделированием в Stormworks.",
        "Всё это — не коммерческие продукты, а личные проекты: что-то рождается из желания добавить в игру нужную деталь, что-то — из интереса разобраться в игровом движке. Иногда это мелочь, иногда — система на сотни узлов, но каждая вещь делается с вниманием к деталям.",
      ],
      tags: ["Garry's Mod", "BeamNG.drive", "Stormworks"],
      projects: [
        {
          id: "gmod-addons",
          name: "Аддоны для Garry's Mod",
          icon: "Puzzle",
          status: "active",
          description:
            "Набор своих аддонов для Garry's Mod: мелкие улучшения и системы, которые делают игру удобнее и веселее.",
          devblog: [
            {
              id: "gmod-addon-1",
              title: "Аддон с полезной мелочью",
              date: "2026-09-22",
              body: "Собрал небольшой аддон для **Garry's Mod** — набор мелких удобств, которых мне не хватало.\n\nНичего глобального, но именно такие мелочи делают игру приятнее. Скоро оформлю в мастерскую.",
            },
          ],
        },
        {
          id: "beamng-mods",
          name: "Моды для BeamNG.drive",
          icon: "Car",
          status: "development",
          description:
            "Моды для BeamNG.drive: конфигурации и детали, которые хочется видеть в игре, но которых там нет.",
          devblog: [
            {
              id: "beamng-mod-1",
              title: "Работа над конфигурацией",
              date: "2026-09-15",
              body: "Работаю над модом для **BeamNG.drive**.\n\nПока это подбор параметров и доводка поведения: нужно, чтобы ехало правдоподобно, а не просто «работало». Много тестов и подгонки.",
            },
          ],
        },
        {
          id: "stormworks-trains",
          name: "Stormworks: поезда и логика",
          icon: "Train",
          status: "development",
          description:
            "Поезда с проработанной логикой и моделированием в Stormworks: системы управления, автоматика, узлы.",
          devblog: [
            {
              id: "storm-1",
              title: "Логика управления поездом",
              date: "2026-09-08",
              body: "Собираю логику управления поездом в **Stormworks**.\n\nЗадача — сделать систему, где поезд ведёт себя предсказуемо: тяга, тормоза, автоматика. Иногда это сотни узлов, но результат того стоит.",
            },
          ],
        },
      ],
      news: [
        {
          id: "addons-news-1",
          title: "Новый аддон для Garry's Mod",
          date: "2026-09-22",
          excerpt: "Набор мелких удобств, оформлю в мастерскую. Такие мелочи делают игру приятнее.",
          body: "Собрал новый аддон для **Garry's Mod** — набор небольших улучшений.\n\nНичего глобального, но именно из мелочей складывается комфорт. Скоро выложу оформленную версию.",
        },
        {
          id: "addons-news-2",
          title: "Мод для BeamNG.drive в работе",
          date: "2026-09-15",
          excerpt: "Довожу поведение до правдоподобия: много тестов и подгонки параметров.",
          body: "Продолжаю работу над модом для **BeamNG.drive**.\n\nОсновное — подбор параметров и доводка поведения. Хочется, чтобы ехало правдоподобно. Это итеративный процесс: тест, правка, снова тест.",
        },
        {
          id: "addons-news-3",
          title: "Stormworks: логика поездов",
          date: "2026-09-08",
          excerpt: "Собираю систему управления: тяга, тормоза и автоматика для предсказуемого поведения.",
          body: "В **Stormworks** строю логику управления поездом.\n\nЦель — предсказуемое поведение: тяга, тормоза, автоматика. Узлов получается много, но система собирается шаг за шагом.",
        },
        {
          id: "addons-news-4",
          title: "Принцип работы над дополнениями",
          date: "2026-08-25",
          excerpt: "Всё начинается с желания добавить в игру нужную деталь или разобраться в движке.",
          body: "Коротко о подходе к дополнениям.\n\nКаждый проект начинается с потребности: либо не хватает детали, либо интересно разобраться в движке. Коммерция тут ни при чём — только интерес и внимание к деталям.",
        },
        {
          id: "addons-news-5",
          title: "Аддоны — личные проекты",
          date: "2026-08-10",
          excerpt: "Дополнения к играм делаются для себя и друзей, а не как продукт для рынка.",
          body: "Напоминание самому себе и читателям: дополнения под Woxel — это **личные проекты**.\n\nОни делаются для себя и друзей, ради интереса. Без обязательств и сроков, в своём темпе.",
        },
      ],
    },
  ],

  footer: {
    copyright: `© ${new Date().getFullYear()} Woxel. Все права защищены.`,
    navTitle: "Навигация",
    navLinks: [
      { label: "Игровые серверы", href: "/games" },
      { label: "Приложения и боты", href: "/apps" },
      { label: "Дополнения", href: "/addons" },
    ],
  },

  ui: {
    common: {
      homeLink: "На главную",
      home: "Главная",
      themeToggle: "Переключить тему",
      toLight: "Включить светлую тему",
      toDark: "Включить тёмную тему",
      mainNav: "Основная навигация",
    },

    auth: {
      loginButton: "Войти",
      loginTitle: "Вход",
      loginDescription: "Войдите в аккаунт Woxel",
      loginField: "Логин или почта",
      loginFieldPlaceholder: "username или you@example.com",
      passwordField: "Пароль",
      loginSubmit: "Войти",
      loginLoading: "Входим…",
      loginDivider: "или",
      discordButton: "Войти через Discord",
      noAccount: "Нет аккаунта?",
      switchToRegister: "Зарегистрироваться",
      loginSuccess: "Вы вошли в аккаунт",
      errAccountBlocked: "Аккаунт заблокирован администратором",
      errCredentials: "Неверный логин/почта или пароль",
      errRateLimited: "Слишком много неверных попыток. Повторите через минуту",
      errUnknown: "Не удалось войти. Попробуйте позже",
      discordNotLinked:
        "Этот Discord-аккаунт ещё не привязан ни к одному пользователю — вход невозможен. Войдите по логину/почте и привяжите Discord в профиле.",
      discordBlocked:
        "Аккаунт, к которому привязан этот Discord, заблокирован администратором.",
      discordUnavailable: "Вход через Discord не настроен на сервере.",
      userMenu: "Меню пользователя",
      profileItem: "Профиль",
      adminPanelItem: "Панель управления",
      logoutItem: "Выйти",
      registerTitle: "Регистрация",
      registerDescription: "Создание аккаунта Woxel",
      emailField: "Почта",
      usernameField: "Логин",
      passwordConfirmField: "Повторите пароль",
      registerSubmit: "Создать аккаунт",
      registerLoading: "Создаём…",
      agreeLabelBefore: "Я согласен с ",
      agreeLinkTerms: "условиями использования",
      agreeLabelMiddle: " и ",
      agreeLinkPrivacy: "политикой конфиденциальности",
      errAgreeRequired:
        "Нужно согласиться с условиями использования и политикой конфиденциальности",
      errPasswordsMismatch: "Пароли не совпадают",
      errServerUnavailable: "Сервер недоступен. Попробуйте позже.",
      registerSuccess: "Аккаунт создан. Теперь войдите.",
      errRegisterFailed: "Не удалось зарегистрироваться",
      registerDisabled: "Регистрация сейчас отключена.",
      registerDisabledHint: "Обратитесь к администратору.",
      tfaTitle: "Двухфакторная аутентификация",
      tfaDescription: "Введите код из приложения-аутентификатора или резервный код",
      tfaField: "Код подтверждения",
      tfaFieldPlaceholder: "000000",
      tfaSubmit: "Подтвердить",
      tfaBack: "Назад",
      errTfaInvalid: "Неверный код подтверждения",
      forgotLink: "Забыли пароль?",
      forgotLinkUnavailable: "Восстановление пароля недоступно (почта не настроена)",
      forgot: {
        title: "Восстановление пароля",
        description: "Укажите почту аккаунта — мы отправим ссылку для сброса пароля.",
        emailLabel: "Почта",
        submit: "Отправить ссылку",
        submitting: "Отправляем…",
        sent: "Если такая почта зарегистрирована, письмо отправлено. Проверьте входящие и папку «Спам».",
        notConfigured: "Отправка почты пока не настроена на сервере.",
        rateLimited: "Слишком много запросов. Повторите чуть позже.",
        backToLogin: "Вернуться к входу",
      },
      reset: {
        title: "Новый пароль",
        description: "Придумайте новый пароль (минимум 8 символов).",
        newPassword: "Новый пароль",
        confirm: "Повторите пароль",
        submit: "Сохранить пароль",
        submitting: "Сохраняем…",
        success: "Пароль изменён. Теперь можно войти с новым паролем.",
        invalid: "Ссылка недействительна или устарела.",
        mismatch: "Пароли не совпадают",
        weak: "Пароль минимум 8 символов",
        toLogin: "Перейти к входу",
      },
      verify: {
        title: "Подтверждение почты",
        submitting: "Подтверждаем почту…",
        success: "Почта подтверждена. Спасибо!",
        invalid: "Ссылка недействительна или устарела.",
        invalidHint: "Запросите новое письмо кнопкой «Отправить письмо» в профиле.",
        goHome: "На главную",
      },
      emailBanner: {
        notVerified: "Почта не подтверждена. Подтвердите её по ссылке из письма.",
        resend: "Отправить письмо",
        sending: "Отправляем…",
        sent: "Письмо отправлено. Проверьте входящие.",
        failed: "Не удалось отправить письмо",
        notConfigured: "Отправка почты пока не настроена",
      },
    },

    profile: {
      title: "Профиль",
      prevProject: "Предыдущий проект",
      nextProject: "Следующий проект",
      editProfile: "Редактировать профиль",
      roleLabel: "Роль",
      noRole: "Без роли",
      projectsParticipation: "Участие в проектах",
      noProjects: "Пока не участвует ни в одном проекте",
      projectProfileSoon:
        "Данные профиля этого проекта появятся позже — сейчас идёт подключение к API сервера проекта.",
      changeAvatar: "Загрузить аватар",
      removeAvatar: "Удалить аватар",
      avatarRemoved: "Аватар удалён",
      avatarRemoveFailed: "Не удалось удалить аватар",
      emailLabel: "Почта",
      usernameLabel: "Логин",
      save: "Сохранить",
      saving: "Сохраняем…",
      cancel: "Отмена",
      changePassword: "Сменить пароль",
      passwordModalTitle: "Смена пароля",
      passwordModalDescription: "Введите текущий пароль и новый (минимум 8 символов)",
      oldPassword: "Старый пароль",
      newPassword: "Новый пароль",
      passwordConfirm: "Повторите новый пароль",
      errPasswordsMismatch: "Пароли не совпадают",
      passwordChanged: "Пароль изменён",
      discordUsernameLabel: "Аккаунт Discord",
      discordLink: "Привязать Discord",
      discordLinking: "Переходим к Discord…",
      discordUnlink: "Отвязать Discord",
      discordUnlinking: "Отвязываем…",
      discordUnlinked: "Discord отвязан. Вход через Discord больше не работает.",
      discordUnlinkFailed: "Не удалось отвязать Discord",
      discordLinkStartFailed: "Не удалось начать привязку. Войдите в аккаунт.",
      discordAlreadyLinked: "Этот Discord уже привязан к другому пользователю",
      discordLinkedToast: "Discord успешно привязан",
      discordNotConfigured: "Привязка Discord недоступна (не настроена на сервере)",
      networkError: "Ошибка сети",
      avatarUpdated: "Аватар обновлён",
      tfa: {
        title: "Двухфакторная аутентификация",
        statusOn: "Включена",
        statusOff: "Выключена",
        description:
          "Дополнительная защита входа: помимо пароля потребуется код из приложения-аутентификатора (Яндекс ID, Google Authenticator и др.).",
        enableButton: "Включить 2FA",
        scanHint: "Отсканируйте QR-код в приложении-аутентификаторе или введите секрет вручную.",
        secretLabel: "Секрет (если не удаётся отсканировать)",
        codeLabel: "Код из приложения",
        confirmButton: "Подтвердить и включить",
        cancel: "Отмена",
        backupTitle: "Резервные коды",
        backupHint:
          "Сохраните эти коды в надёжном месте. Каждый код используется один раз — для входа, если нет доступа к приложению.",
        disableButton: "Отключить 2FA",
        disableCodeLabel: "Текущий код для подтверждения отключения",
        disableCodePlaceholder: "Код из приложения или резервный",
        confirmDisable: "Отключить",
        error: "Не удалось выполнить операцию",
        enabledToast: "Двухфакторная аутентификация включена",
        disabledToast: "Двухфакторная аутентификация отключена",
      },
      dangerTitle: "Опасная зона",
      deleteAccount: "Удаление аккаунта",
      deleteAccountConfirmHint:
        "Аккаунт и связанные данные (профили, роли, резервные коды, аватар) будут удалены безвозвратно. Это действие необратимо.",
      deleteAccountButton: "Удалить аккаунт навсегда",
      deleting: "Удаляем…",
      deleteAccountSuccess: "Аккаунт удалён. Вы вышли из системы.",
      deleteAccountError: "Не удалось удалить аккаунт",
      deleteLastDev: "Нельзя удалить последний аккаунт разработчика",
      deleteConfirmLabel: "Для подтверждения введите ваш логин",
      deleteConfirmPlaceholder: "Ваш логин",
      deleteConfirmMismatch: "Логин введён неверно",
    },

    directions: {
      readMore: "Подробнее",
      projectsTitle: "Текущие проекты",
      noProjects: "В этом направлении пока нет проектов.",
      noProjectsFound: "Проекты не найдены.",
      filterStatus: "Статус",
      filterTag: "Тег",
      allStatuses: "Все статусы",
      allTags: "Все теги",
      devblogButton: "DevBlog",
      devblogTitle: "DevBlog проекта",
      devblogEmpty: "Записей пока нет — проект только начинается.",
      showMore: "Показать ещё",
      newsTitle: "Последние новости",
      newsEmpty: "Новостей пока нет.",
      allNewsButton: "Все новости",
      newsDialogTitle: "Новости направления",
      statusActive: "Активен",
      statusDevelopment: "В разработке",
      statusPlanned: "В планах",
      statusFrozen: "Заморожен",
      statusCancelled: "Отменён",
      statusArchived: "Архивирован",
      archiveButton: "Архив проектов",
      archiveTitle: "Архив проектов",
      archiveEmpty: "Архив проектов пока пуст.",
    },

    legal: {
      termsTitle: "Условия использования",
      termsDescription: "Правила использования сайта Woxel",
      privacyTitle: "Политика конфиденциальности",
      privacyDescription: "Обработка персональных данных",
      termsBody: [
        "1. Общие положения",
        "Настоящие Условия использования (далее — «Условия») определяют правила использования сайта, размещённого в сети «Интернет» по адресу woxel.ru и его поддоменах (далее — «Сайт»), и регулируют отношения между Администрацией Сайта и любым лицом, использующим Сайт (далее — «Пользователь»).",
        "Администрация Сайта — физическое лицо, самостоятельно развивающее некоммерческий хобби-проект «Woxel» (далее — «Администрация»). Контакт для обращений: support@woxel.ru.",
        "Используя Сайт любым способом (просмотр страниц, регистрация, вход в личный кабинет), Пользователь подтверждает, что ознакомился с настоящими Условиями, принимает их в полном объёме и обязуется их соблюдать. Регистрация учётной записи означает акцепт настоящих Условий (ст. 438 ГК РФ).",
        "2. Термины",
        "Сайт — совокупность программных средств, размещённых по указанному адресу. Учётная запись — создаваемая Пользователем запись, дающая доступ к функциям личного кабинета. Контент — любые материалы, размещаемые на Сайте, включая тексты, изображения и данные.",
        "3. Предмет",
        "Администрация предоставляет Пользователю возможность безвозмездного доступа к функциям Сайта и его сервисам (просмотр публичной информации, регистрация, личный кабинет и связанные с ними функции). Сайт предоставляется на условиях «как есть» (as is).",
        "4. Регистрация и учётная запись",
        "Для доступа к отдельным функциям требуется регистрация. При регистрации Пользователь указывает адрес электронной почты и имя пользователя (логин) и самостоятельно задаёт пароль. Пользователь обязан указывать достоверные данные и не передавать доступ к своей учётной записи третьим лицам.",
        "Пользователь несёт ответственность за сохранность своего пароля и за все действия, совершённые с использованием его учётной записи.",
        "В целях безопасности Администрация может запрашивать подтверждение адреса электронной почты и поддерживает двухфакторную аутентификацию (TFA2).",
        "Регистрация на Сайте осуществляется Пользователем самостоятельно и не требует указания возраста и иных документов. Администрация не располагает сведениями о возрасте Пользователя и не имеет технической возможности достоверно его установить или контролировать. Лица, не достигшие 14 лет, используют Сайт только с согласия своих законных представителей (родителей, усыновителей, опекунов); получение такого согласия лежит на Пользователе и его законных представителях.",
        "5. Права и обязанности Пользователя",
        "Пользователь обязуется не нарушать законодательство Российской Федерации и права третьих лиц; не предпринимать действий, направленных на нарушение работы Сайта, несанкционированный доступ, обход ограничений доступа, распространение вредоносного кода; не размещать материалы, содержащие оскорбления, угрозы, незаконный контент, и не использовать Сайт для спама и рассылок.",
        "Пользователь вправе в любой момент прекратить использование Сайта и удалить свою учётную запись.",
        "6. Права и обязанности Администрации",
        "Администрация вправе изменять, дополнять или приостанавливать работу Сайта и его отдельных функций; устанавливать и изменять правила использования; ограничивать или прекращать доступ Пользователя при нарушении настоящих Условий; удалять аккаунт и контент по запросу Пользователя либо при наличии законных оснований.",
        "Администрация принимает разумные меры для стабильной работы Сайта, однако не гарантирует бесперебойную и безошибочную работу, поскольку Сайт (включая техническую инфраструктуру) поддерживается самостоятельно в рамках хобби-проекта.",
        "7. Интеллектуальная собственность",
        "Материалы Сайта, созданные Администрацией, охраняются законодательством об интеллектуальной собственности. Права на содержимое, размещённое Пользователем, остаются у Пользователя; размещая такое содержимое, Пользователь предоставляет Администрации право хранить и отображать его в рамках работы Сайта.",
        "8. Персональные данные",
        "Обработка персональных данных Пользователя осуществляется в соответствии с Политикой конфиденциальности Сайта и Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных».",
        "9. Ответственность",
        "Сайт предоставляется «как есть». В максимально допустимых законом пределах Администрация не несёт ответственности за возможные убытки, возникшие в связи с использованием или невозможностью использования Сайта, включая перерывы в работе, потерю данных и ошибочные действия третьих лиц.",
        "Администрация не несёт ответственности за содержимое внешних ресурсов, ссылки на которые могут размещаться на Сайте.",
        "10. Изменение Условий",
        "Администрация вправе изменять настоящие Условия. Новая редакция вступает в силу с момента её опубликования на Сайте, если не указано иное. Продолжение использования Сайта после вступления изменений в силу означает согласие Пользователя с новой редакцией.",
        "11. Прекращение",
        "Пользователь вправе удалить учётную запись в любой момент. Администрация вправе прекратить доступ Пользователя при нарушении настоящих Условий. Прекращение доступа не освобождает стороны от обязательств, возникших до такого прекращения.",
        "12. Применимое право и споры",
        "Настоящие Условия регулируются законодательством Российской Федерации. Споры разрешаются путём переговоров, а при недостижении согласия — в порядке, установленном законодательством РФ.",
        "13. Контакты",
        "Обращения и вопросы по работе Сайта: support@woxel.ru.",
      ],
      termsNote:
        "Редакция от 10.10.2026. Используя Сайт, вы принимаете настоящие Условия.",
      privacyBody: [
        "1. Общие положения",
        "Настоящая Политика конфиденциальности (далее — «Политика») описывает, какие персональные данные обрабатываются при использовании сайта woxel.ru и его поддоменов (далее — «Сайт»), в каких целях, как они защищаются и какие права есть у субъекта персональных данных.",
        "Оператор персональных данных — физическое лицо, развивающее некоммерческий хобби-проект «Woxel» (далее — «Оператор»). Контакт для обращений по вопросам обработки персональных данных: support@woxel.ru.",
        "Политика составлена в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных».",
        "2. Правовые основания обработки",
        "Обработка осуществляется на основании согласия субъекта персональных данных, а также в связи с исполнением договора (Условий использования), стороной которого является субъект персональных данных.",
        "3. Категории обрабатываемых персональных данных",
        "Обрабатываются следующие данные, предоставляемые Пользователем и создаваемые при использовании Сайта: адрес электронной почты; имя пользователя (логин/никнейм); пароль (хранится исключительно в виде необратимого хеша и не может быть восстановлен в исходном виде); изображение пользователя (аватар) — при добровольной загрузке; идентификатор и имя пользователя Discord — при добровольной привязке аккаунта; секрет и статус двухфакторной аутентификации (TOTP); дата и время регистрации, статус активности учётной записи.",
        "Дополнительно, автоматически, обрабатываются технические данные, необходимые для работы и защиты Сайта: сетевой адрес (IP-адрес) и данные файлов cookie технического назначения.",
        "Не обрабатываются специальные категории персональных данных (сведения о здоровье, национальной принадлежности, политических, религиозных или философских убеждениях, судимости) и биометрические персональные данные.",
        "4. Цели обработки",
        "Персональные данные обрабатываются в единственной цели — предоставление доступа пользователя к сайту домена woxel.ru и его сервисам (регистрация, аутентификация, личный кабинет и связанные с ними функции сайта).",
        "5. Категории субъектов",
        "Субъектами персональных данных являются посетители и зарегистрированные пользователи Сайта, а также лица, обратившиеся по вопросам работы сервиса.",
        "6. Перечень действий и способ обработки",
        "Обработка включает: сбор, запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передачу (предоставление, доступ), блокирование, удаление и уничтожение данных.",
        "Способ обработки — смешанный: с использованием средств автоматизации (веб-сервер и база данных) и без использования средств автоматизации (обработка обращений вручную).",
        "7. Файлы cookie и технические данные",
        "Сайт использует только технические файлы cookie, необходимые для его работы. Такие файлы cookie не используются для рекламы или профилирования. IP-адрес обрабатывается для защиты Сайта от автоматизированных атак и ограничения частоты запросов.",
        "8. Передача персональных данных третьим лицам",
        "Персональные данные третьим лицам не передаются. Передача возможна только в случаях, прямо предусмотренных законодательством Российской Федерации (например, по законному запросу уполномоченных государственных органов).",
        "9. Трансграничная передача",
        "Трансграничная передача персональных данных не осуществляется.",
        "10. Место хранения данных",
        "Персональные данные хранятся на собственном сервере Оператора, физически размещённом на территории Российской Федерации. Локализация базы данных обеспечена в соответствии с требованиями законодательства РФ.",
        "11. Меры защиты персональных данных",
        "Оператором принимаются правовые, организационные и технические меры защиты, включая: разграничение прав доступа; защиту канала передачи данных по протоколу HTTPS (TLS); хранение паролей в виде необратимого хеша; парольную политику и ограничение частоты запросов; журналирование событий безопасности; резервное копирование и восстановление данных; ограничение физического доступа к серверу.",
        "12. Сроки хранения и удаление данных",
        "Персональные данные хранятся до тех пор, пока существует учётная запись, — до (а) обращения Пользователя об удалении данных либо (б) законного требования (распоряжения) об удалении. После наступления любого из этих событий данные удаляются или уничтожаются.",
        "Пользователь может самостоятельно удалить свою учётную запись из личного кабинета, а также направить запрос об удалении данных на контактный адрес Оператора.",
        "13. Права субъекта персональных данных",
        "Пользователь вправе: получать информацию об обработке своих персональных данных; требовать уточнения, блокирования или удаления данных, если они неполны, устарели, неточны или незаконно получены; отозвать согласие на обработку; обжаловать действия Оператора в уполномоченный орган по защите прав субъектов персональных данных (Роскомнадзор) или в суд.",
        "Для реализации своих прав Пользователь направляет обращение на контактный адрес Оператора. Обращение рассматривается в сроки, установленные законодательством Российской Федерации.",
        "14. Несовершеннолетние",
        "Регистрация на Сайте осуществляется пользователем самостоятельно и не требует указания возраста, документа, удостоверяющего личность, или сведений о законных представителях. Оператор не располагает сведениями о возрасте пользователя и не имеет возможности достоверно его установить или контролировать. Обработка персональных данных лиц, не достигших 14 лет, осуществляется с согласия их законных представителей. Законные представители вправе обратиться к Оператору для получения информации об обработке данных своего ребёнка и их удаления.",
        "15. Изменения Политики",
        "Оператор вправе изменять настоящую Политику. Новая редакция вступает в силу с момента её опубликования на Сайте. Действующая редакция всегда доступна на Сайте.",
        "16. Контакты",
        "Вопросы и обращения по вопросам обработки персональных данных: support@woxel.ru.",
      ],
      privacyNote:
        "Редакция от 10.10.2026. Актуальная версия всегда доступна на этой странице.",
      footerTerms: "Условия использования",
      footerPrivacy: "Политика конфиденциальности",
      footerNav: "Дополнительные ссылки",
      discordAria: "Discord-сервер Woxel",
      discordTitle: "Наш Discord",
      discordNotConfigured: "Ссылка на Discord не настроена (панель → Настройки сайта)",
      telegramAria: "Telegram-канал Woxel",
      telegramTitle: "Наш Telegram",
      telegramNotConfigured: "Ссылка на Telegram не настроена (панель → Настройки сайта)",
    },

    notification: {
      bannerAria: "Уведомление сайта",
    },

    lock: {
      title: "Woxel Works",
      description:
        "Сайт временно закрыт для посещения. Чтобы попасть на него, введите пароль доступа.",
      passwordLabel: "Пароль доступа",
      passwordPlaceholder: "Введите пароль",
      submit: "Войти",
      submitting: "Проверяем…",
      errorWrong: "Неверный пароль",
      errorRate: "Слишком много попыток. Попробуйте позже.",
      errorNetwork: "Ошибка сети. Попробуйте ещё раз.",
    },

    loading: {
      label: "Загружаем сайт…",
    },

    errors: {
      notFoundTitle: "Страница не найдена",
      notFoundText: "Возможно, страница удалена или ссылка указана неверно.",
      backHome: "На главную",
      serverTitle: "Что-то пошло не так",
      serverText: "Произошла ошибка при загрузке страницы. Попробуйте ещё раз.",
      retry: "Повторить",
    },
  },
};
